import Image from "next/image";
import Link from "next/link";
import { coverImageObjectPosition, coverImageSrc } from "@/lib/cover-image-meta";
import { cn } from "@/lib/utils";

interface TransfersArticleCardProps {
  article: {
    id: string;
    title: string;
    excerpt?: string | null;
    coverImage?: string | null;
    coverImageMeta?: unknown;
    slug: string;
    category?: { name: string } | null;
    publishedAt?: Date | string | null;
    gridSize: string;
    openable?: boolean | null;
  };
  className?: string;
  index?: number;
  labelOverride?: string;
}

function CardContent({
  article,
  labelOverride,
}: Pick<TransfersArticleCardProps, "article" | "labelOverride">) {
  const isOpenable = article.openable !== false;
  const isFeatured = article.gridSize === "featured";
  const showImage = Boolean(article.coverImage);
  const imageFrameClassName = "aspect-[16/9]";
  const label = labelOverride ?? (article.openable === false ? "Rövid pletyka" : article.category?.name);

  return (
    <>
      {showImage ? (
        <div
          className={cn(
            "relative w-full shrink-0 overflow-hidden rounded-none bg-[#E8E0D2]",
            imageFrameClassName,
          )}
        >
          <Image
            src={coverImageSrc(article.coverImage, article.coverImageMeta, isFeatured ? "800" : "400")}
            alt={article.title}
            fill
            sizes={isFeatured ? "(max-width: 768px) 100vw, 420px" : "(max-width: 768px) 100vw, 360px"}
            className={cn("object-cover transition-transform duration-300", isOpenable && "group-hover:scale-[1.02]")}
            style={{ objectPosition: coverImageObjectPosition(article.coverImageMeta) }}
          />
        </div>
      ) : null}

      {label ? (
        <span className="text-[10px] font-bold uppercase leading-none tracking-[0.24em] text-[#A4243B]">
          {label}
        </span>
      ) : null}

      <h3
        className={cn(
          "overflow-hidden font-display font-black leading-[1.02] text-[#111111] transition-colors",
          isFeatured
            ? "line-clamp-3 max-w-[13ch] text-5xl sm:text-6xl md:max-w-[13ch] md:text-[2.8rem]"
            : "line-clamp-3 max-w-[17ch] text-3xl md:max-w-none md:text-[1.42rem]",
          isOpenable && "group-hover:text-[#A4243B]",
        )}
      >
        {article.title}
      </h3>

      {article.excerpt ? (
        <p
          className={cn(
            "text-[#6B6256]",
            isFeatured
              ? "line-clamp-3 max-w-xl text-base leading-6 md:max-w-[38ch]"
              : "line-clamp-2 max-w-[34ch] text-sm leading-[1.15rem] md:max-w-none",
          )}
        >
          {article.excerpt}
        </p>
      ) : null}

      {article.publishedAt ? (
        <span className="mt-auto pt-2 text-[11px] font-medium uppercase leading-none tracking-[0.16em] text-[#8A8175]/80">
          {new Date(article.publishedAt).toLocaleDateString("hu-HU")}
        </span>
      ) : null}
    </>
  );
}

export function TransfersArticleCard({ article, className, labelOverride }: TransfersArticleCardProps) {
  const isFeatured = article.gridSize === "featured";
  const cardClassName = cn(
    "group flex flex-col rounded-none border-t border-black/10 bg-transparent pt-4 shadow-none",
    isFeatured ? "gap-4 pb-1" : "gap-2 pb-1",
    className,
  );

  if (article.openable === false) {
    return (
      <article className={cardClassName}>
        <CardContent article={article} labelOverride={labelOverride} />
      </article>
    );
  }

  return (
    <Link
      href={`/article/${article.slug}`}
      className={cardClassName}
    >
      <CardContent article={article} labelOverride={labelOverride} />
    </Link>
  );
}
